import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { withEmailCodeVerified } from '../util/emailCodeVerified';
import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { rememberSignedInUser, sendAuthDiag } from '../util/authDiag';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { retryOn429 } from '../util/sharetribeSafeQuery';
import { isUserAuthorized } from '../util/userHelpers';
import { pickOwnListeningProfile, summarizeOwnListeningProfile } from '../util/ownListeningProfile';
import { queryOwnListeningProfiles } from '../util/queryOwnListeningProfiles';
import {
  getStatesNeedingProviderAttention,
  getStatesNeedingCustomerAttention,
} from '../transactions/transaction';
import {
  applyFavoriteIdsToUser,
  applyFavoriteProfileIdsToUser,
  favoriteEntityId,
  favoriteListingIdsFromUser,
  favoriteProfileIdsFromUser,
  toggleFavoriteIds,
} from '../util/favorites';
import {
  HIDDEN_INBOX_TX_IDS_KEY,
  applyHiddenInboxIdsToUser,
  hiddenInboxTransactionIdsFromUser,
  hideInboxTransaction,
  inboxEntityId,
  normalizeHiddenInboxIds,
} from '../util/inboxArchive';
import { hideInboxConversation } from '../util/api';
import { applyPreferredNavModeToUser, NAV_MODES, preferredNavModeData } from '../util/siteNavMode';
import {
  applyGuidelinesToUser,
  pickGuidelinesProtectedData,
} from '../util/signupConsent';

import { authInfo } from './auth.duck';
import { applyPendingVerifyToken } from '../util/pendingEmailVerify';
import { updateStripeConnectAccount } from './stripeConnectAccount.duck';

// ================ Helper Functions ================ //

const mergeCurrentUser = (oldCurrentUser, rawNewCurrentUser) => {
  // 6-digit email code counts the same as Sharetribe's link (Oct 11 2026).
  const newCurrentUser = rawNewCurrentUser ? withEmailCodeVerified(rawNewCurrentUser) : rawNewCurrentUser;
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

// ================ Async Thunks ================ //

//////////////////////////////////////////////////////////////////////
// Fetch ownListings to check if currentUser has a Listening profile //
//////////////////////////////////////////////////////////////////////

const fetchCurrentUserHasListingsPayloadCreator = (_, thunkAPI) => {
  const { getState, extra: sdk, rejectWithValue } = thunkAPI;
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.resolve({ hasListings: false, ownListing: null, fetched: true });
  }

  // Include draft / pending / closed. Never treat a 429 as “no listing”.
  return queryOwnListeningProfiles(sdk)
    .then(({ listings, error }) => {
      if (error || listings == null) {
        return rejectWithValue(storableError(error || new Error('ownListings query failed')));
      }
      const picked = pickOwnListeningProfile(listings.map(ensureOwnListing));
      const ownListing = summarizeOwnListeningProfile(picked);
      return { hasListings: !!ownListing, ownListing, fetched: true };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasListingsThunk = createAsyncThunk(
  'user/fetchCurrentUserHasListings',
  fetchCurrentUserHasListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasListingsThunk()).unwrap();
};

///////////////////////////////////////////////////////////
// Fetch transactions to check if currentUser has orders //
///////////////////////////////////////////////////////////

const fetchCurrentUserHasOrdersPayloadCreator = (_, { getState, extra: sdk, rejectWithValue }) => {
  if (!getState().user.currentUser) {
    return Promise.resolve({ hasOrders: false });
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return retryOn429(() => sdk.transactions.query(params))
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      return { hasOrders: !!hasOrders };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasOrdersThunk = createAsyncThunk(
  'user/fetchCurrentUserHasOrders',
  fetchCurrentUserHasOrdersPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasOrdersThunk()).unwrap();
};

/////////////////////////////////////////////////////////////////////////////////////
// Fetch transactions in specific states to check if currentUser has notifications //
/////////////////////////////////////////////////////////////////////////////////////

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

const fetchCurrentUserNotificationsPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  const statesNeedingProviderAttention = getStatesNeedingProviderAttention() || [];
  const statesNeedingCustomerAttention = getStatesNeedingCustomerAttention() || [];

  const paramsForSales = {
    only: 'sale',
    states: statesNeedingProviderAttention.map(state => `state/${state}`).join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };
  const paramsForOrders = {
    only: 'order',
    states: statesNeedingCustomerAttention.map(state => `state/${state}`).join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  return retryOn429(() => sdk.transactions.query(paramsForSales))
    .then(sales =>
      retryOn429(() => sdk.transactions.query(paramsForOrders)).then(orders => [sales, orders])
    )
    .then(([sales, orders]) => {
      const saleNotificationsCount = sales.data.data.length;
      const orderNotificationsCount = orders.data.data.length;
      return { saleNotificationsCount, orderNotificationsCount };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserNotificationsThunk = createAsyncThunk(
  'user/fetchCurrentUserNotifications',
  fetchCurrentUserNotificationsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserNotificationsThunk()).unwrap();
};

const fetchCurrentUserPayloadCreator = (options, thunkAPI) => {
  const { getState, dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve(state.user.currentUser);
  }

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    return Promise.resolve(null);
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
      'variants.listing-card',
      'variants.listing-card-2x',
      'variants.default',
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
    'imageVariant.listing-card': sdkUtil.objectQueryString({
      w: 400,
      h: 400,
      fit: 'crop',
    }),
    'imageVariant.listing-card-2x': sdkUtil.objectQueryString({
      w: 800,
      h: 800,
      fit: 'crop',
    }),
  };

  return retryOn429(() => sdk.currentUser.show(parameters))
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(updateStripeConnectAccount(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      rememberSignedInUser(currentUser.id.uuid);
      // A verify link opened without a session saved its token: apply it now.
      applyPendingVerifyToken(sdk, currentUser, () =>
        dispatch(fetchCurrentUser({ enforce: true }))
      );
      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (updateHasListings !== false) {
          const userState = getState().user || {};
          const knownReady = userState.currentUserHasListings && userState.currentUserOwnListing;
          const knownEmpty =
            userState.currentUserHasListingsFetched &&
            !userState.currentUserHasListingsError &&
            userState.currentUserHasListings === false;
          if (!knownReady && !knownEmpty) {
            dispatch(fetchCurrentUserHasListings());
          }
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      sendAuthDiag('fetch_current_user_failed', { status: e?.status, reason: e?.statusText || e?.message });
      return rejectWithValue(storableError(e));
    });
};

export const fetchCurrentUserThunk = createAsyncThunk(
  'user/fetchCurrentUser',
  fetchCurrentUserPayloadCreator
);
// Backward compatible wrapper for the thunk
/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserThunk(options)).unwrap();
};

/////////////////////////////////////////////
// Send verification email to currentUser //
/////////////////////////////////////////////

const sendVerificationEmailPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => ({}))
    .catch(e => rejectWithValue(storableError(e)));
};
export const sendVerificationEmailThunk = createAsyncThunk(
  'user/sendVerificationEmail',
  sendVerificationEmailPayloadCreator,
  {
    condition: (_, { getState }) => {
      return !getState()?.user?.sendVerificationEmailInProgress;
    },
  }
);

// Backward compatible wrapper for the thunk
export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  return dispatch(sendVerificationEmailThunk()).unwrap();
};

// ================ Slice ================ //

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUser: null,
    currentUserShowTimestamp: 0,
    currentUserShowError: null,
    currentUserHasListings: false,
    currentUserHasListingsFetched: false,
    currentUserOwnListing: null,
    currentUserHasListingsError: null,
    currentUserSaleNotificationCount: 0,
    currentUserOrderNotificationCount: 0,
    currentUserNotificationCountError: null,
    currentUserHasOrders: null, // This is not fetched unless unverified emails exist
    currentUserHasOrdersError: null,
    sendVerificationEmailInProgress: false,
    sendVerificationEmailError: null,
  },
  reducers: {
    clearCurrentUser: state => {
      state.currentUser = null;
      state.currentUserShowError = null;
      state.currentUserHasListings = false;
      state.currentUserHasListingsFetched = false;
      state.currentUserOwnListing = null;
      state.currentUserHasListingsError = null;
      state.currentUserSaleNotificationCount = 0;
      state.currentUserOrderNotificationCount = 0;

      state.currentUserNotificationCountError = null;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
    },
    setCurrentUserHasOrders: state => {
      state.currentUserHasOrders = true;
    },
    setCurrentUserHasListings: (state, action) => {
      state.currentUserHasListings = !!action.payload;
      if (action.payload) {
        state.currentUserHasListingsFetched = true;
      }
    },
    setCurrentUserOwnListing: (state, action) => {
      if (action.payload) {
        state.currentUserOwnListing = action.payload;
        state.currentUserHasListingsFetched = true;
        if (action.payload.state && action.payload.state !== LISTING_STATE_DRAFT) {
          state.currentUserHasListings = true;
        }
      }
    },
  },
  extraReducers: builder => {
    builder
      // fetchCurrentUser
      .addCase(fetchCurrentUserThunk.pending, state => {
        state.currentUserShowError = null;
      })
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
        state.currentUserShowTimestamp = action.payload ? new Date().getTime() : 0;
      })
      .addCase(fetchCurrentUserThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserShowError = action.payload;
      })
      // fetchCurrentUserHasListings
      .addCase(fetchCurrentUserHasListingsThunk.pending, state => {
        state.currentUserHasListingsError = null;
      })
      .addCase(fetchCurrentUserHasListingsThunk.fulfilled, (state, action) => {
        // Do not downgrade a true set by the dashboard own-listings query.
        // A lagged empty query (Login-as / 429 / string states) used to hide the toggle.
        state.currentUserHasListingsFetched = true;
        if (action.payload.hasListings) {
          state.currentUserHasListings = true;
        }
        if (action.payload.ownListing) {
          state.currentUserOwnListing = action.payload.ownListing;
        }
      })
      .addCase(fetchCurrentUserHasListingsThunk.rejected, (state, action) => {
        console.error(action.payload);
        // Keep any listing we already have. A 429 is not “not set up”.
        state.currentUserHasListingsFetched = false;
        state.currentUserHasListingsError = action.payload;
      })
      // fetchCurrentUserNotifications
      .addCase(fetchCurrentUserNotificationsThunk.pending, state => {
        state.currentUserNotificationCountError = null;
      })
      .addCase(fetchCurrentUserNotificationsThunk.fulfilled, (state, action) => {
        state.currentUserSaleNotificationCount = action.payload.saleNotificationsCount;
        state.currentUserOrderNotificationCount = action.payload.orderNotificationsCount;
      })
      .addCase(fetchCurrentUserNotificationsThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserNotificationCountError = action.payload;
      })
      // fetchCurrentUserHasOrders
      .addCase(fetchCurrentUserHasOrdersThunk.pending, state => {
        state.currentUserHasOrdersError = null;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.fulfilled, (state, action) => {
        state.currentUserHasOrders = action.payload.hasOrders;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserHasOrdersError = action.payload;
      })
      // sendVerificationEmail
      .addCase(sendVerificationEmailThunk.pending, state => {
        state.sendVerificationEmailInProgress = true;
        state.sendVerificationEmailError = null;
      })
      .addCase(sendVerificationEmailThunk.fulfilled, state => {
        state.sendVerificationEmailInProgress = false;
      })
      .addCase(sendVerificationEmailThunk.rejected, (state, action) => {
        state.sendVerificationEmailInProgress = false;
        state.sendVerificationEmailError = action.payload;
      });
  },
});

export default userSlice.reducer;

export const {
  clearCurrentUser,
  setCurrentUser,
  setCurrentUserHasOrders,
  setCurrentUserHasListings,
  setCurrentUserOwnListing,
} = userSlice.actions;

/**
 * Favorite or unfavorite a Listening profile. Signed-in only.
 * Writes privateData.favoriteListingIds and keeps currentUser in sync.
 *
 * @param {string} listingId
 * @returns {Function}
 */
export const toggleFavoriteListing = listingId => (dispatch, getState, sdk) => {
  const currentUser = getState()?.user?.currentUser;
  const id = favoriteEntityId(listingId);
  if (!currentUser?.id?.uuid || !id || !sdk?.currentUser?.updateProfile) {
    return Promise.resolve(favoriteListingIdsFromUser(currentUser));
  }
  const nextIds = toggleFavoriteIds(favoriteListingIdsFromUser(currentUser), id);
  dispatch(setCurrentUser(applyFavoriteIdsToUser(currentUser, nextIds)));
  return sdk.currentUser
    .updateProfile({ privateData: { favoriteListingIds: nextIds } }, { expand: true })
    .then(() =>
      dispatch(
        fetchCurrentUser({
          enforce: true,
          updateHasListings: false,
          updateNotifications: false,
        })
      )
    )
    .then(() => nextIds)
    .catch(e => {
      dispatch(setCurrentUser(currentUser));
      throw storableError(e);
    });
};

/**
 * Favorite or unfavorite a Talking profile (/u). Signed-in only.
 * Writes privateData.favoriteProfileIds so a dual-role talker heart
 * never opens that person's Listening listing.
 *
 * @param {string} userId
 * @returns {Function}
 */
export const toggleFavoriteProfile = userId => (dispatch, getState, sdk) => {
  const currentUser = getState()?.user?.currentUser;
  const id = favoriteEntityId(userId);
  if (!currentUser?.id?.uuid || !id || !sdk?.currentUser?.updateProfile) {
    return Promise.resolve(favoriteProfileIdsFromUser(currentUser));
  }
  const nextIds = toggleFavoriteIds(favoriteProfileIdsFromUser(currentUser), id);
  dispatch(setCurrentUser(applyFavoriteProfileIdsToUser(currentUser, nextIds)));
  return sdk.currentUser
    .updateProfile({ privateData: { favoriteProfileIds: nextIds } }, { expand: true })
    .then(() =>
      dispatch(
        fetchCurrentUser({
          enforce: true,
          updateHasListings: false,
          updateNotifications: false,
        })
      )
    )
    .then(() => nextIds)
    .catch(e => {
      dispatch(setCurrentUser(currentUser));
      throw storableError(e);
    });
};

/**
 * Hide an Inbox conversation for this signed-in user only.
 * Sharetribe cannot delete transactions, so we persist hidden ids on
 * privateData and keep a server/local cache as backup.
 *
 * @param {string|{uuid?: string, id?: {uuid?: string}}} transactionId
 * @returns {Function}
 */
/**
 * Remember last-used Talker / Listener hamburger mode on protectedData.preferred.
 *
 * @param {'talker'|'listener'} mode
 * @returns {Function}
 */
export const savePreferredNavMode = mode => (dispatch, getState, sdk) => {
  if (!NAV_MODES.includes(mode)) {
    return Promise.resolve(null);
  }
  const currentUser = getState()?.user?.currentUser;
  if (!currentUser?.id) {
    return Promise.resolve(mode);
  }
  dispatch(setCurrentUser(applyPreferredNavModeToUser(currentUser, mode)));
  if (!sdk?.currentUser?.updateProfile) {
    return Promise.resolve(mode);
  }
  return sdk.currentUser
    .updateProfile(preferredNavModeData(mode), { expand: true })
    .then(() =>
      dispatch(
        fetchCurrentUser({
          enforce: true,
          updateHasListings: false,
          updateNotifications: false,
        })
      )
    )
    .then(() => mode)
    .catch(e => {
      dispatch(setCurrentUser(currentUser));
      throw storableError(e);
    });
};

/**
 * Stamp Community Guidelines acceptance on protectedData. Once per account.
 *
 * @returns {Function}
 */
/** Free first session: the talker ticks "I'm 18 or older" (Sam, Oct 21 2026, TEST). */
export const attestAdult = ({ nowIntent = false } = {}) => (dispatch, getState, sdk) => {
  const currentUser = getState()?.user?.currentUser;
  if (!currentUser?.id || !sdk?.currentUser?.updateProfile) {
    return Promise.resolve(null);
  }
  const at = new Date().toISOString();
  const protectedData = nowIntent
    ? { adultSelfAttestedAt: at, freeTalkNowIntentAt: at }
    : { adultSelfAttestedAt: at };
  return sdk.currentUser
    .updateProfile({ protectedData }, { expand: true })
    .then(() => dispatch(fetchCurrentUser({ enforce: true, updateHasListings: false, updateNotifications: false })))
    .then(() => {
      try {
        require('../util/talkerFunnel').trackTalkerFunnel('adult_confirm', { detail: nowIntent ? 'now' : 'later' });
      } catch (e) {
        // ignore
      }
      return protectedData;
    })
    .catch(e => {
      throw storableError(e);
    });
};

export const acceptCommunityGuidelines = () => (dispatch, getState, sdk) => {
  const currentUser = getState()?.user?.currentUser;
  if (!currentUser?.id) {
    return Promise.resolve(null);
  }
  const protectedData = pickGuidelinesProtectedData();
  dispatch(setCurrentUser(applyGuidelinesToUser(currentUser, protectedData)));
  if (!sdk?.currentUser?.updateProfile) {
    return Promise.resolve(protectedData);
  }
  return sdk.currentUser
    .updateProfile({ protectedData }, { expand: true })
    .then(() =>
      dispatch(
        fetchCurrentUser({
          enforce: true,
          updateHasListings: false,
          updateNotifications: false,
        })
      )
    )
    .then(() => protectedData)
    .catch(e => {
      dispatch(setCurrentUser(currentUser));
      throw storableError(e);
    });
};

export const hideInboxTransactionForUser = transactionId => (dispatch, getState, sdk) => {
  const currentUser = getState()?.user?.currentUser;
  const txId = inboxEntityId(transactionId);
  if (!txId) {
    return Promise.resolve([]);
  }
  hideInboxTransaction(inboxEntityId(currentUser), txId);
  const nextIds = normalizeHiddenInboxIds([
    ...hiddenInboxTransactionIdsFromUser(currentUser),
    txId,
  ]);
  if (currentUser?.attributes?.profile) {
    dispatch(setCurrentUser(applyHiddenInboxIdsToUser(currentUser, nextIds)));
  }
  const profilePromise =
    currentUser?.id && sdk?.currentUser?.updateProfile
      ? sdk.currentUser
          .updateProfile({ privateData: { [HIDDEN_INBOX_TX_IDS_KEY]: nextIds } }, { expand: true })
          .catch(() => null)
      : Promise.resolve(null);
  const serverPromise = hideInboxConversation(txId).catch(() => []);
  return Promise.all([profilePromise, serverPromise]).then(() => nextIds);
};

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};
